import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertTriangle, Trash2, Play } from 'lucide-react';
import { Session } from '@/types/session';
import { formatTime } from '@/utils/sessionTimeUtils';

interface OrphanedSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  orphanedSession: Session | null;
  onContinueSession: (session: Session) => void;
  onDiscardSession: () => void;
}

export function OrphanedSessionModal({
  isOpen,
  onClose,
  orphanedSession,
  onContinueSession,
  onDiscardSession,
}: OrphanedSessionModalProps) {
  if (!orphanedSession) {
    return null;
  }

  const handleContinue = () => {
    onContinueSession(orphanedSession);
    onClose();
  };

  const handleDiscard = () => {
    onDiscardSession();
    onClose();
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'paused':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="md:max-w-md lg:max-w-lg bg-gray-900 border-gray-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center border border-yellow-500/30">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="text-lg font-semibold">Unfinished Session</div>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            We found an unfinished session. Would you like to continue or discard it?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Session Details */}
          <div className="bg-gray-800/50 rounded-lg p-4 space-y-3 border border-gray-700/50">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-white">{orphanedSession.goal}</h3>
              <Badge className={getStatusColor(orphanedSession.status)}>
                {orphanedSession.status}
              </Badge>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{formatDuration(orphanedSession.duration)}</span>
              </div>
              
              {orphanedSession.started_at && (
                <div className="flex items-center gap-1">
                  <Play className="h-4 w-4" />
                  <span>
                    Started {new Date(orphanedSession.started_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleDiscard}
            className="flex items-center gap-2 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
          >
            <Trash2 className="h-4 w-4" />
            Discard Session
          </Button>
          <Button
            onClick={handleContinue}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Play className="h-4 w-4" />
            Continue Session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OrphanedSessionModal;
